import { useCustom, useTranslate } from '@refinedev/core';
import { Input, Select, Space, Switch, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { usePortalLanguage } from '../../components/WhatsNew/useWhatsNew';
import { LEARN_ARTICLE_IDS } from '../homework/learnArticles';
import { ASSESSMENT_TARGETS, TOOL_TARGETS } from '../homework/model';
import { VideoTargetInput } from '../homework/VideoTargetInput';
import { ALL_CATEGORIES, filterByCategory, keepIfInCategory } from '../worksheets/categoryFilter';
import { worksheetLanguageArg } from '../worksheets/model';
import { WorksheetCategorySelect } from '../worksheets/WorksheetCategorySelect';

/**
 * E34 · the pathway step editor's Target picker — "the Target picker is the assignment drawer's" (the
 * Builder's Behavior): the same lists the Assign drawer offers, scoped by kind — the Toolkit tools, the
 * instruments the practice may assign, the Learn articles, the worksheet library (the practice's own +
 * CopingCard's), a URL, a video (a Learn clip or a YouTube / Vimeo link — F-172); a task has none. The same copy keys (`homework.target.*`, `homework.articles.*`).
 */
type Props = {
  kind: string;
  value: string | null;
  onChange: (value: string | null, suggestedTitle?: string) => void;
  testId: string;
};

export const StepTargetPicker = ({ kind, value, onChange, testId }: Props) => {
  const t = useTranslate();
  // Q-ws-lang-2 A: the portal's language by default; the switch lists every language (reset when the kind changes)
  const portalLanguage = usePortalLanguage();
  const [allLanguages, setAllLanguages] = useState(false);
  // P039: the Category filter opens on All and resets with the kind
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  useEffect(() => {
    setAllLanguages(false);
    setCategory(ALL_CATEGORIES);
  }, [kind]);
  const { data: worksheetData } = useCustom<
    Array<{ templateId: string; name: string; category: string; owner: string }>
  >({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: kind === 'worksheet' },
    meta: {
      operation: 'worksheetTemplates',
      fields: ['templateId', 'name', 'category', 'owner'],
      variables: {
        language: {
          name: 'language',
          type: 'String',
          value: worksheetLanguageArg(allLanguages, undefined, portalLanguage),
        },
      },
    },
  });
  const worksheets = useMemo(() => worksheetData?.data ?? [], [worksheetData]);

  const options = useMemo((): { value: string; label: string }[] => {
    switch (kind) {
      case 'worksheet':
        return filterByCategory(worksheets, category).map((w) => ({
          value: w.templateId,
          label: `${w.name} · ${t(`worksheets.category.${w.category}`)}${
            w.owner === 'library' ? ` · ${t('worksheets.library.owners.library')}` : ''
          }`,
        }));
      case 'tool':
        return TOOL_TARGETS.map((x) => ({ value: x, label: t(`homework.target.tool.${x}`) }));
      case 'assessment':
        return ASSESSMENT_TARGETS.map((x) => ({ value: x, label: t(`homework.target.assessment.${x}`) }));
      case 'reading':
        return LEARN_ARTICLE_IDS.map((x) => ({ value: x, label: t(`homework.articles.${x}`) }));
      default:
        return [];
    }
  }, [kind, t, worksheets, category]);

  if (kind === 'task') {
    return (
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        {t('homework.assign.taskNoTarget')}
      </Typography.Paragraph>
    );
  }
  if (kind === 'video') {
    // the Builder has NO client: VideoTargetInput offers the therapist's portal-language Learn catalogue (Q-cf-2);
    // choosing another catalogue is a later round
    return <VideoTargetInput value={value} onChange={onChange} testId={testId} />;
  }
  if (kind === 'link') {
    return (
      <Input
        value={value ?? ''}
        placeholder={t('homework.assign.linkPlaceholder')}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testId}
      />
    );
  }
  const select = (
    <Select
      style={{ width: '100%' }}
      showSearch
      optionFilterProp="label"
      value={value ?? undefined}
      placeholder={t('homework.assign.targetPlaceholder')}
      onChange={(v: string) => {
        const suggested =
          kind === 'worksheet'
            ? worksheets.find((w) => w.templateId === v)?.name
            : options.find((o) => o.value === v)?.label;
        onChange(v, suggested);
      }}
      options={options}
      data-testid={testId}
    />
  );
  if (kind !== 'worksheet') return select;
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <WorksheetCategorySelect
        templates={worksheets}
        value={category}
        onChange={(c) => {
          setCategory(c);
          // Q-wcat-2 A: a chosen worksheet outside the new category clears
          if (value && keepIfInCategory(value, worksheets, c) === undefined) onChange(null);
        }}
        data-testid={`${testId}-category`}
      />
      {select}
      <Space size={8}>
        <Switch
          size="small"
          checked={allLanguages}
          onChange={(on) => {
            setAllLanguages(on);
            setCategory(ALL_CATEGORIES);
            onChange(null); // the list changes: a pick from the other list no longer stands
          }}
          data-testid={`${testId}-all-languages`}
        />
        <Typography.Text style={{ fontSize: 12 }}>{t('worksheets.library.allLanguages')}</Typography.Text>
      </Space>
    </Space>
  );
};
